import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { formatDate } from '@/lib/format-date';
import type { Exhibition } from '@/types';

type ExhibitionFilter = 'all' | 'current' | 'upcoming' | 'archive';

const ExhibitionsPage: React.FC = () => {
  const { t, localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const exhibitions = useAdminStore((s) => s.exhibitions);
  const [filter, setFilter] = useState<ExhibitionFilter>('all');

  const now = new Date();

  const categorized = useMemo(() => {
    const current: Exhibition[] = [];
    const upcoming: Exhibition[] = [];
    const archive: Exhibition[] = [];

    exhibitions.forEach((e) => {
      const start = new Date(e.startDate);
      const end = new Date(e.endDate);
      if (now >= start && now <= end) {
        current.push(e);
      } else if (start > now) {
        upcoming.push(e);
      } else {
        archive.push(e);
      }
    });

    const byNewest = (a: Exhibition, b: Exhibition): number =>
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime();

    current.sort(byNewest);
    upcoming.sort(byNewest);
    archive.sort(byNewest);

    return { current, upcoming, archive };
  }, [exhibitions]);

  const filteredExhibitions = useMemo(() => {
    if (filter === 'current') return categorized.current;
    if (filter === 'upcoming') return categorized.upcoming;
    if (filter === 'archive') return categorized.archive;
    // 'all' — show in order: current, upcoming, archive
    return [...categorized.current, ...categorized.upcoming, ...categorized.archive];
  }, [filter, categorized]);

  const filters: Array<{ id: ExhibitionFilter; label: string; count: number }> = [
    { id: 'all', label: t.exhibitions.all, count: exhibitions.length },
    { id: 'current', label: t.exhibitions.current, count: categorized.current.length },
    { id: 'upcoming', label: t.exhibitions.upcoming, count: categorized.upcoming.length },
    { id: 'archive', label: t.exhibitions.archive, count: categorized.archive.length },
  ];

  const getStatus = (exhibition: Exhibition): 'current' | 'upcoming' | 'archive' => {
    const start = new Date(exhibition.startDate);
    const end = new Date(exhibition.endDate);
    if (now >= start && now <= end) return 'current';
    if (start > now) return 'upcoming';
    return 'archive';
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-20 px-4 sm:px-6 md:px-24">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-10 sm:mb-16"
        >
          <h1 className="text-3xl sm:text-5xl md:text-8xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
            {t.exhibitions.title}
          </h1>
        </motion.div>

        {/* Filter tabs */}
        <div className="flex items-center gap-2 mb-12 overflow-x-auto pb-1 scrollbar-hide">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-[0.12em] whitespace-nowrap transition-all duration-300 ${
                filter === f.id
                  ? 'bg-black dark:bg-white text-white dark:text-black shadow-sm'
                  : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
              }`}
            >
              {f.id === 'current' && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
              {f.label}
              <span className={`tabular-nums ${filter === f.id ? 'opacity-50' : 'opacity-40'}`}>({f.count})</span>
            </button>
          ))}
        </div>

        {/* Exhibition cards */}
        {filteredExhibitions.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
              No exhibitions found
            </p>
          </div>
        ) : filter === 'all' ? (
          <div className="space-y-16">
            {categorized.current.map((exhibition, i) => (
              <ExhibitionCard key={exhibition._id} exhibition={exhibition} index={i} localize={localize} status="current" />
            ))}
            {categorized.upcoming.length > 0 && (
              <>
                <h2 className="text-[10px] font-bold uppercase tracking-[0.4em] text-black/30 dark:text-white/30 border-b border-black/5 dark:border-white/5 pb-3 mt-8">
                  {t.exhibitions.upcoming}
                </h2>
                {categorized.upcoming.map((exhibition, i) => (
                  <ExhibitionCard key={exhibition._id} exhibition={exhibition} index={i} localize={localize} status="upcoming" />
                ))}
              </>
            )}
            {categorized.archive.length > 0 && (
              <>
                <h2 className="text-[10px] font-bold uppercase tracking-[0.4em] text-black/30 dark:text-white/30 border-b border-black/5 dark:border-white/5 pb-3 mt-8">
                  {t.exhibitions.archive}
                </h2>
                {categorized.archive.map((exhibition, i) => (
                  <ExhibitionCard key={exhibition._id} exhibition={exhibition} index={i} localize={localize} status="archive" />
                ))}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-16">
            {filteredExhibitions.map((exhibition, i) => (
              <ExhibitionCard
                key={exhibition._id}
                exhibition={exhibition}
                index={i}
                localize={localize}
                status={getStatus(exhibition)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface ExhibitionCardProps {
  exhibition: Exhibition;
  index: number;
  localize: (text: { ka: string; en: string }) => string;
  status: 'current' | 'upcoming' | 'archive';
}

const ExhibitionCard: React.FC<ExhibitionCardProps> = ({
  exhibition,
  index,
  localize,
  status,
}) => {
  const { t, language } = useTranslation();
  const startDate = formatDate(exhibition.startDate, language);
  const endDate = formatDate(exhibition.endDate, language);

  const statusLabel = status === 'current' ? t.exhibitions.current : status === 'upcoming' ? t.exhibitions.upcoming : t.exhibitions.archive;
  const statusColors = status === 'current'
    ? 'bg-green-50 dark:bg-green-500/10 text-green-600'
    : status === 'upcoming'
      ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600'
      : 'bg-black/[0.04] dark:bg-white/[0.06] text-black/40 dark:text-white/40';

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15, duration: 0.8 }}
    >
      <Link to={`/exhibitions/${exhibition.slug}`} className="flex flex-col md:flex-row gap-6 sm:gap-10 group">
        <div className={`w-full md:w-[320px] lg:w-[380px] h-[220px] md:h-[240px] lg:h-[280px] rounded-2xl overflow-hidden bg-[#f9f9f9] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5 shrink-0 ${
          status === 'archive' ? 'grayscale-[0.5]' : ''
        }`}>
          <img
            src={exhibition.poster}
            alt={localize(exhibition.title)}
            className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
          />
        </div>
        <div className="flex-1 flex flex-col justify-center space-y-4 sm:space-y-5">
          {status !== 'current' && (
            <span className={`inline-flex items-center self-start gap-2 px-4 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${statusColors}`}>
              {statusLabel}
            </span>
          )}
          <h3 className="text-xl sm:text-2xl md:text-3xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
            {localize(exhibition.title)}
          </h3>
          {(exhibition.excerpt?.en || exhibition.excerpt?.ka) ? (
            <p className="text-sm sm:text-base text-black/50 dark:text-white/50 font-light leading-relaxed line-clamp-3">
              {localize(exhibition.excerpt)}
            </p>
          ) : (
            <p className="text-sm sm:text-base text-black/50 dark:text-white/50 font-light leading-relaxed line-clamp-3">
              {localize(exhibition.description)}
            </p>
          )}
          <div className="flex flex-col gap-3 text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">
            <div className="flex items-center gap-2">
              <Calendar size={14} />
              {startDate} &mdash; {endDate}
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={14} />
              {localize(exhibition.location)}
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            {exhibition.artists.map((artist) => (
              <span
                key={artist._id}
                className="px-4 py-2 bg-black/5 dark:bg-white/5 rounded-full text-[9px] font-bold uppercase tracking-wider"
              >
                {localize(artist.name)}
              </span>
            ))}
          </div>
          <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-black/30 dark:text-white/30 group-hover:text-black dark:group-hover:text-white transition-colors">
            {t.exhibitions.viewExhibition} <ArrowRight size={12} />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export default ExhibitionsPage;
